import {UI, type PartialOptions} from "../../../stem-core/ui/UIBase";
import type {ApiRequestError} from "../../../client/connection/BlinkApiClient";
import {DashboardTitle} from "../../common/DashboardTitle";
import type {Merchant} from "../../../client/state/MerchantStore";
import {merchantService} from "../../misc/MerchantService";
import {
    apiMerchantEmailingBreakdown,
    apiUpdateMerchantEmailingSettings,
    MerchantEmailingSettings,
    type EmailingBreakdown,
    type GetEmailingBreakdownRequest
} from "../../../client/state/merchant/MerchantEmailingSettingsStore";
import {BlinkInputField} from "../../common/Input";
import {TextInput} from "../../../stem-core/ui/input/Input";
import {autoredraw} from "../../../stem-core/decorators/AutoRedraw";
import {CollapsiblePanel} from "../../../stem-core/ui/collapsible/CollapsiblePanel";
import {TopLevelTabArea} from "../../common/theme/TabAreaStyle";
import {DateRangeInput, DateRangeInterval, DefaultMerchantDateRangeIntervals} from "../../ui/input/DateRangeInput";
import {Level} from "../../../stem-core/ui/Constants";
import {Button} from "../../../stem-core/ui/button/Button";
import {
    apiMerchantDeleteEmailSender,
    MerchantEmailSender
} from "../../../client/state/merchant/MerchantEmailSender";
import {LEFT_COLUMN} from "../../common/theme/TableStyle";
import {SimpleTable} from "../../ui/SimpleTable";
import {MerchantAddEmailSenderModal} from "../../common/modals/MerchantAddEmailSenderModal";
import {MerchantEditEmailSenderModal} from "../../common/modals/MerchantEditEmailSenderModal";
import {Toast} from "../../../blinkpay/ui/Toast";
import {Select} from "../../../stem-core/ui/input/Input";
import {EmailSendingStoreObject} from "../../../client/state/merchant/MerchantEmailCampaignStore";
import {EmailSendingObjectStatistics} from "./EmailCampaignSendingPanel";
import {MultiEmailAddressInput} from "../../common/Input";
import {InfoBlockGroup} from "../../common/InfoBlock";
import {MerchantAudience} from "../../../client/state/merchant/MerchantAudienceStore";
import {MultiselectInput} from "../../common/MultiselectInput";
import {Chart} from "../../ui/Chart";
import {StemDate} from "../../../stem-core/time/Date";
import {Theme} from "../../../stem-core/ui/style/Theme";
import type {ColumnTuple} from "../../../stem-core/base/ColumnHandler";


@autoredraw(MerchantEmailSender)
class MerchantEmailSenderEditor extends UI.Element {
    async deleteEmailSender(emailSender: MerchantEmailSender) {
        await apiMerchantDeleteEmailSender({
            merchantId: merchantService.getMerchant().id,
            emailSenderId: emailSender.id,
        });
    }

    render() {
        const merchant = merchantService.getMerchant();
        const emailSenders = MerchantEmailSender.filterBy({merchantId: merchant.id});
        const columns: ColumnTuple<MerchantEmailSender>[] = [
            ["Name", emailSender => emailSender.name || "-", LEFT_COLUMN],
            ["Email", emailSender => emailSender.emailAddress, LEFT_COLUMN],
            ["Actions", emailSender => {
                return [
                    <Button onClick={() => MerchantEditEmailSenderModal.show({emailSender})} label="Edit"/>,
                    <Button onClick={() => this.deleteEmailSender(emailSender)} label="Delete"/>
                ]
            }]
        ]

        return (
            <div style={{margin: "20px 0px"}}>
                <Button style={{marginBottom: 12}} onClick={() => MerchantAddEmailSenderModal.show()}
                        label="New email sender"/>
                <SimpleTable<MerchantEmailSender> columns={columns} entries={emailSenders}/>
            </div>
        )
    }
}

interface MerchantEmailingSettingsEditorOptions {
    merchantEmailingSettings?: MerchantEmailingSettings;
}

@autoredraw(MerchantEmailSender)
class MerchantEmailingSettingsEditor extends UI.Element<MerchantEmailingSettingsEditorOptions> {
    declare emailSenderSelector: Select<MerchantEmailSender>;

    inputRefs: Record<string, any> = {};
    blink_default_sending_email = "Blink default"

    getDefaultOptions(): PartialOptions<MerchantEmailingSettingsEditor> {
        return {
            title: "General Settings",
        }
    }

    async updateEmailingSettings() {
        const request = {
            defaultSenderId: this.getSelectedEmailSender()?.id,
            emailReplyTo: this.inputRefs.emailReplyToInput?.getValue().trim(),
            bccEmails: this.inputRefs.bccEmailsInput.getValue(),
        }

        try {
            await apiUpdateMerchantEmailingSettings(request);
            Toast.showSuccess("Saved");
        } catch (error) {
            Toast.showError(error);
        }

    }

    getSelectedEmailSender() {
        return this.emailSenderSelector?.getValue();
    }

    render() {
        const {merchantEmailingSettings} = this.options;
        const defaultSender = merchantEmailingSettings.defaultSender;
        const senderDropdownOptions = MerchantEmailSender.all();

        return [
            <div>
                Control the default when Blink sends emails on your behalf.
                All these settings can be overwritten by individual templates/campaigns.
            </div>,
            <MerchantEmailSenderEditor/>,
            <div>
                <Button label="Save changes" onClick={() => this.updateEmailingSettings()}/>
                <Button level={Level.SECONDARY} label="Discard changes"/>
            </div>,
            <BlinkInputField label="Default sending email address">
                {senderDropdownOptions.length === 0 ?
                    <TextInput disabled={true} placeholder={this.blink_default_sending_email}/> :
                    <Select ref="emailSenderSelector"
                            options={senderDropdownOptions}
                            onChange={() => this.redraw()}
                            selected={this.getSelectedEmailSender() || defaultSender}
                    />
                }
            </BlinkInputField>,
            <div>
                <BlinkInputField label="Default sending name">
                    <TextInput disabled={true}
                               placeholder={this.blink_default_sending_email}
                               value={this.getSelectedEmailSender()?.name || defaultSender?.name}/>
                </BlinkInputField>
            </div>,
            <div>
                <BlinkInputField label="Default reply-to email address">
                    <TextInput ref={{parent: this.inputRefs, name: "emailReplyToInput"}}
                               value={merchantEmailingSettings.emailReplyTo || ""}
                    />
                </BlinkInputField>
            </div>,
            <div>
                <BlinkInputField label="Default bcc addresses">
                    <MultiEmailAddressInput ref={{parent: this.inputRefs, name: "bccEmailsInput"}}
                                            value={merchantEmailingSettings.bccEmails}
                    />
                </BlinkInputField>
            </div>,
        ];
    }
}


interface MerchantEmailOverviewSectionOptions {
    title?: string;
    merchant?: Merchant;
}

class MerchantEmailOverviewSection extends UI.Element<MerchantEmailOverviewSectionOptions> {
    declare audiencesInput: MultiselectInput;
    declare dateRangeInput: DateRangeInput;

    lastResponse: EmailingBreakdown | null = null;
    lastError: ApiRequestError | null = null;

    async applyFilters() {
        const {startDate, endDate} = this.dateRangeInput.getValue();
        this.lastError = this.lastResponse = null;
        this.redraw();
        const filters: GetEmailingBreakdownRequest = {
            startDate,
            endDate,
        };
        const selectedAudiences = this.audiencesInput.getValue();
        if (selectedAudiences.length !== MerchantAudience.filterBy({merchant: merchantService.getMerchant()}).length) {
            filters.audienceIds = selectedAudiences.map(audience => audience.id);
        }
        try {
            this.lastResponse = await apiMerchantEmailingBreakdown(filters);
            this.lastResponse.dateHistogram = this.lastResponse.dateHistogram?.map(d => ({
                ...d,
                date: new StemDate(d.date)
            })) || [];
        } catch (error) {
            this.lastError = error;
        }

        this.redraw();
    }

    renderSummary() {
        const {lastResponse, lastError} = this;
        if (lastError) {
            return "Error in loading statistics: " + lastError;
        }
        if (!lastResponse) {
            return null;
        }

        const emailStatistics = new EmailSendingStoreObject(lastResponse);

        return [
            <div style={{display: "flex"}}>
                <EmailSendingObjectStatistics emailSendingObject={emailStatistics} style={{display: "inline-flex"}}/>
                <InfoBlockGroup label="Newsletter subscribers" entries={[
                    ["Newly subscribed", lastResponse.subscribersCount || 0],
                    ["Unsubscribed", lastResponse.unsubscribersCount || 0],
                ]}/>
            </div>
        ];
    }

    renderNewSubscribersChart() {
        if (!this.lastResponse) {
            return null;
        }
        return [
            <DashboardTitle
                title="Newsletter size changes"
                description="Visualize the growth of your newsletter audiences over time"
            />,
            <Chart {...this.dateRangeInput.getValue()}
                   data={this.lastResponse.dateHistogram}
                   tooltipOptions={{displayTotal: false}}
                   categories={[{
                       name: "Unsubscribed",
                       value: "unsubscribersCount",
                       color: Theme.props.CHART_BAR_BACKGROUND_COLOR_1,
                   }, {
                       name: "Newly subscribed",
                       value: "subscribersCount",
                       color: Theme.props.CHART_BAR_BACKGROUND_COLOR_3,
                   }]}/>];
    }

    render() {
        const merchant = merchantService.getMerchant();
        const allAudiences = MerchantAudience.filterBy({merchant});
        return [
            <h2>Filter to get general information.</h2>,
            <BlinkInputField>
                <DateRangeInput
                    ref="dateRangeInput"
                    style={{maxWidth: 360}}
                    options={DefaultMerchantDateRangeIntervals(merchant)}
                    selectedInterval={DateRangeInterval.LAST_30_DAYS}
                    onChange={() => this.applyFilters()}
                />
            </BlinkInputField>,
            <CollapsiblePanel title="Filter by audience" collapsed={true}>
                <MultiselectInput options={allAudiences} initialValue={allAudiences} ref="audiencesInput"
                                  onChange={() => this.applyFilters()}/>
            </CollapsiblePanel>,
            <CollapsiblePanel title="Summary & Statistics" collapsed={false} style={{marginTop: 36}}>
                {this.renderSummary()}
            </CollapsiblePanel>,

            this.renderNewSubscribersChart(),
        ]
    }

    async onMount() {
        await this.applyFilters();
    }
}

export class MerchantEmailsPage extends UI.Element {
    render() {
        const merchant = merchantService.getMerchant();
        const merchantEmailingSettings = MerchantEmailingSettings.find(obj => obj.merchantId === merchant.id);

        return [
            <DashboardTitle
                title="Emailing"
                description="Statistics on emailing"
            />,
            <TopLevelTabArea>
                <MerchantEmailOverviewSection title="Overview" merchant={merchant}/>
                <MerchantEmailingSettingsEditor title="General Settings"
                                                merchantEmailingSettings={merchantEmailingSettings}/>
            </TopLevelTabArea>
        ]
    }
}
